import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { collection, doc, getCountFromServer, getDoc, getDocs, orderBy, query } from 'firebase/firestore';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Dialog } from '@mui/material';
import { db } from '../../firebase/firebase';
import VisorRA from '../VisorRA';
import { describeGeoError } from './geoLocationError';
import { useVisitor } from '../../context/VisitorContext';
import { recordDiscovery, markRestored } from '../../services/discoveryService';
import { getActiveEvents } from '../../services/eventsService';
import { joinPresence, subscribePresence, incrementRestoredCount, subscribeRestoredCount } from '../../services/presenceService';
import { zoneIndexForTheta, RESTORATION_TOTAL_STEPS, RESTORATION_ZOOM_RATIO } from '../../utils/restoration';
import DiscoveryOverlay from '../game/DiscoveryOverlay';
import RestorationHud from '../game/RestorationHud';
import RestorationCompleteOverlay from '../game/RestorationCompleteOverlay';
import ActiveEventBanner from '../events/ActiveEventBanner';
import './geoScenes.css';
import { loadGoogleMaps } from './googleMaps';

const VIEWER_VISITED_KEY_PREFIX = 'geoSceneVisited_';
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
const PROXIMITY_EXIT_BUFFER_METERS = 8;
const PROXIMITY_REOPEN_COOLDOWN_MS = 5000;
const PROXIMITY_ENTER_CONFIRM_SAMPLES = 2;
const PROXIMITY_EXIT_CONFIRM_SAMPLES = 4;
const PROXIMITY_MAX_GPS_ACCURACY_METERS = 30;
const PROXIMITY_MIN_OPEN_MS = 10000;
const PROXIMITY_AR_RETURN_GRACE_MS = 8000;
const PROXIMITY_FILTER_ALPHA = 0.35;
const GEO_DIAGNOSTIC_MODE_KEY = 'geoDiagnosticMode';

const haversineMeters = (aLat, aLng, bLat, bLng) => {
  const toRad = (v) => (v * Math.PI) / 180;
  const R = 6371000;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const aa = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(aa));
};

const bearingDegrees = (aLat, aLng, bLat, bLng) => {
  const toRad = (v) => (v * Math.PI) / 180;
  const toDeg = (v) => (v * 180) / Math.PI;
  const phi1 = toRad(aLat);
  const phi2 = toRad(bLat);
  const lambda1 = toRad(aLng);
  const lambda2 = toRad(bLng);
  const y = Math.sin(lambda2 - lambda1) * Math.cos(phi2);
  const x = Math.cos(phi1) * Math.sin(phi2)
    - Math.sin(phi1) * Math.cos(phi2) * Math.cos(lambda2 - lambda1);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
};

const ORIENTATION_ALIGNED_THRESHOLD_DEG = 25;

function GeoSceneMap({
  center,
  placements,
  routeGuided,
  currentRouteTargetId,
  visitedPlacementIds,
  userPosition,
  onMarkerClick
}) {
  const mapHolderRef = useRef(null);
  const mapRef = useRef(null);
  const mapsApiRef = useRef(null);
  const markersRef = useRef([]);
  const polylineRef = useRef(null);
  const userMarkerRef = useRef(null);
  const [error, setError] = useState(null);
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    let mounted = true;
    if (!GOOGLE_MAPS_API_KEY) {
      setError('Falta configurar REACT_APP_GOOGLE_MAPS_API_KEY para mostrar Google Maps.');
      return () => {};
    }

    loadGoogleMaps(GOOGLE_MAPS_API_KEY)
      .then((maps) => {
        if (!mounted || !mapHolderRef.current) return;
        mapsApiRef.current = maps;

        const centerObj = { lat: Number(center[0]), lng: Number(center[1]) };
        if (!mapRef.current) {
          mapRef.current = new maps.Map(mapHolderRef.current, {
            center: centerObj,
            zoom: 17,
            mapTypeControl: false,
            fullscreenControl: false,
            streetViewControl: true,
            gestureHandling: 'greedy',
            zoomControl: true,
            zoomControlOptions: { position: maps.ControlPosition.RIGHT_BOTTOM },
            streetViewControlOptions: { position: maps.ControlPosition.RIGHT_BOTTOM }
          });
          setMapReady(true);
        } else {
          mapRef.current.panTo(centerObj);
        }

        markersRef.current.forEach((m) => m.setMap(null));
        markersRef.current = [];
        if (polylineRef.current) {
          polylineRef.current.setMap(null);
          polylineRef.current = null;
        }

        placements.forEach((p, idx) => {
          const isTarget = routeGuided && currentRouteTargetId === p.id;
          const isVisited = visitedPlacementIds.includes(p.id);
          const marker = new maps.Marker({
            map: mapRef.current,
            position: { lat: Number(p.lat), lng: Number(p.lng) },
            label: routeGuided ? { text: String(idx + 1), color: '#fff', fontWeight: '700' } : undefined,
            icon: {
              path: maps.SymbolPath.CIRCLE,
              scale: routeGuided ? 10 : 8,
              fillColor: isTarget ? '#dc2626' : isVisited ? '#059669' : '#2563eb',
              fillOpacity: 1,
              strokeColor: '#ffffff',
              strokeWeight: 2
            }
          });
          marker.addListener('click', () => onMarkerClick(p));
          markersRef.current.push(marker);
        });

        if (routeGuided && placements.length > 1) {
          polylineRef.current = new maps.Polyline({
            map: mapRef.current,
            path: placements.map((p) => ({ lat: Number(p.lat), lng: Number(p.lng) })),
            geodesic: true,
            strokeColor: '#2563eb',
            strokeOpacity: 0.8,
            strokeWeight: 4
          });
        }
      })
      .catch((e) => {
        console.error(e);
        if (mounted) setError('No se pudo cargar Google Maps.');
      });

    return () => {
      mounted = false;
    };
  }, [center, placements, routeGuided, currentRouteTargetId, visitedPlacementIds, onMarkerClick]);

  useEffect(() => {
    const maps = mapsApiRef.current;
    if (!maps || !mapRef.current || !userPosition) return;
    const pos = { lat: Number(userPosition[0]), lng: Number(userPosition[1]) };
    if (!userMarkerRef.current) {
      userMarkerRef.current = new maps.Marker({
        map: mapRef.current,
        position: pos,
        zIndex: 999,
        icon: {
          path: maps.SymbolPath.CIRCLE,
          scale: 7,
          fillColor: '#22d3ee',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 2
        }
      });
    } else {
      userMarkerRef.current.setPosition(pos);
    }
  }, [userPosition, mapReady]);

  useEffect(() => {
    return () => {
      markersRef.current.forEach((m) => m.setMap(null));
      markersRef.current = [];
      if (userMarkerRef.current) {
        userMarkerRef.current.setMap(null);
        userMarkerRef.current = null;
      }
      if (polylineRef.current) {
        polylineRef.current.setMap(null);
        polylineRef.current = null;
      }
    };
  }, []);

  const recenterOnUser = () => {
    if (!mapRef.current || !userPosition) return;
    mapRef.current.panTo({ lat: Number(userPosition[0]), lng: Number(userPosition[1]) });
    mapRef.current.setZoom(18);
  };

  if (error) return <div className="geo-street-error">{error}</div>;
  return (
    <div className="geo-scene-map-wrap">
      <div ref={mapHolderRef} className="geo-scene-map" />
      {mapReady && userPosition && (
        <button type="button" className="geo-map-recenter-btn" onClick={recenterOnUser} title="Centrar en mi ubicación">
          <i className="bi bi-crosshair" />
        </button>
      )}
    </div>
  );
}

export default function GeoSceneViewer() {
  const { sceneId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { visitorId, refreshProfile } = useVisitor();

  const [scene, setScene] = useState(null);
  const [placements, setPlacements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activePlacement, setActivePlacement] = useState(null);
  const [activePlacementSource, setActivePlacementSource] = useState('manual');
  const [discoveredPlacement, setDiscoveredPlacement] = useState(null);
  const [discoveryXpResult, setDiscoveryXpResult] = useState(null);
  const [discoveryFragmentsCount, setDiscoveryFragmentsCount] = useState(0);
  const [activeAnchors, setActiveAnchors] = useState([]);
  const [activeAnchorsLoading, setActiveAnchorsLoading] = useState(false);
  const [eventXpMultiplier, setEventXpMultiplier] = useState(1);

  // Minijuego de restauración + presencia multijugador para el placement
  // que está abierto en AR (activePlacement).
  const [restorationZones, setRestorationZones] = useState(new Set());
  const [restorationZoomed, setRestorationZoomed] = useState(false);
  const [restorationResult, setRestorationResult] = useState(null);
  const [presenceList, setPresenceList] = useState([]);
  const [restoredCommunityCount, setRestoredCommunityCount] = useState(0);
  const restorationBaselineRadiusRef = useRef(null);
  const restorationDoneRef = useRef(false);

  // Se pide apenas monta el componente (no depende del render de
  // ActiveEventBanner) para minimizar la ventana en la que una detección de
  // proximidad muy rápida podría no tener todavía el bono de XP del evento.
  useEffect(() => {
    let alive = true;
    getActiveEvents()
      .then((list) => {
        if (!alive) return;
        const max = list.reduce((m, ev) => Math.max(m, Number(ev.xpMultiplier) || 1), 1);
        setEventXpMultiplier(max);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const [userPosition, setUserPosition] = useState(null);
  const [userAccuracyMeters, setUserAccuracyMeters] = useState(null);
  const [positionError, setPositionError] = useState(null);
  const [tracking, setTracking] = useState(false);
  const [visitedPlacementIds, setVisitedPlacementIds] = useState([]);
  const [arPresenting, setArPresenting] = useState(false);
  const [diagnosticMode, setDiagnosticMode] = useState(false);
  const [filteredDistanceByPlacement, setFilteredDistanceByPlacement] = useState({});
  const [compassHeading, setCompassHeading] = useState(null);
  const [compassEnabled, setCompassEnabled] = useState(false);
  const [compassError, setCompassError] = useState(null);

  const proximityStateRef = useRef({});
  const proximityLastOpenRef = useRef({});
  const proximityOpenStartedAtRef = useRef({});
  const arLastStopAtRef = useRef(0);
  const seedAutoHandledRef = useRef(false);
  const distanceFilterRef = useRef({});

  const autoRequested = searchParams.get('auto') === '1';
  const seedLat = Number(searchParams.get('lat'));
  const seedLng = Number(searchParams.get('lng'));
  const hasSeedCoords = Number.isFinite(seedLat) && Number.isFinite(seedLng);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const sceneSnap = await getDoc(doc(db, 'geoScenes', sceneId));
        if (!sceneSnap.exists()) {
          setError('Escena no encontrada');
          setScene(null);
          setPlacements([]);
          return;
        }
        const sceneData = { id: sceneSnap.id, ...(sceneSnap.data() || {}) };
        if (!sceneData.isPublic) {
          setError('Esta escena no está publicada');
          setScene(null);
          setPlacements([]);
          return;
        }
        setScene(sceneData);

        const placementSnap = await getDocs(collection(db, 'geoScenes', sceneId, 'placements'));
        const list = placementSnap.docs
          .map((d) => ({ id: d.id, ...(d.data() || {}) }))
          .filter((p) => Number.isFinite(Number(p.lat)) && Number.isFinite(Number(p.lng)) && p.modelUrl);
        setPlacements(list);
        setError(null);
      } catch (e) {
        console.error(e);
        setError('No se pudo cargar el visor geográfico');
      } finally {
        setLoading(false);
      }
    })();
  }, [sceneId]);

  useEffect(() => {
    seedAutoHandledRef.current = false;
  }, [sceneId]);

  useEffect(() => {
    try {
      setDiagnosticMode(localStorage.getItem(GEO_DIAGNOSTIC_MODE_KEY) === '1');
    } catch {}
  }, []);

  const requestCompassPermission = async () => {
    try {
      setCompassError(null);
      if (typeof DeviceOrientationEvent !== 'undefined'
        && typeof DeviceOrientationEvent.requestPermission === 'function') {
        const res = await DeviceOrientationEvent.requestPermission();
        if (res !== 'granted') {
          setCompassError('No se autorizó el acceso a la brújula.');
          return;
        }
      }
      setCompassEnabled(true);
    } catch (e) {
      console.error(e);
      setCompassError('No se pudo activar la brújula.');
    }
  };

  useEffect(() => {
    if (!compassEnabled) return undefined;
    const onOrientation = (ev) => {
      if (typeof ev.webkitCompassHeading === 'number') {
        setCompassHeading(ev.webkitCompassHeading);
        return;
      }
      if (typeof ev.alpha === 'number') {
        setCompassHeading((360 - ev.alpha + 360) % 360);
      }
    };
    window.addEventListener('deviceorientationabsolute', onOrientation, true);
    window.addEventListener('deviceorientation', onOrientation, true);
    return () => {
      window.removeEventListener('deviceorientationabsolute', onOrientation, true);
      window.removeEventListener('deviceorientation', onOrientation, true);
    };
  }, [compassEnabled]);

  useEffect(() => {
    if (!sceneId) return;
    try {
      const raw = sessionStorage.getItem(`${VIEWER_VISITED_KEY_PREFIX}${sceneId}`);
      if (!raw) {
        setVisitedPlacementIds([]);
        return;
      }
      const parsed = JSON.parse(raw);
      setVisitedPlacementIds(Array.isArray(parsed) ? parsed : []);
    } catch {
      setVisitedPlacementIds([]);
    }
  }, [sceneId]);

  useEffect(() => {
    if (!sceneId) return;
    try {
      sessionStorage.setItem(`${VIEWER_VISITED_KEY_PREFIX}${sceneId}`, JSON.stringify(visitedPlacementIds));
    } catch {}
  }, [sceneId, visitedPlacementIds]);

  const orderedPlacements = useMemo(() => {
    const list = [...placements];
    list.sort((a, b) => {
      const ao = Number(a.routeOrder ?? 9999);
      const bo = Number(b.routeOrder ?? 9999);
      if (ao !== bo) return ao - bo;
      return String(a.title || '').localeCompare(String(b.title || ''));
    });
    return list;
  }, [placements]);

  const mapCenter = useMemo(() => {
    if (Array.isArray(scene?.center) && scene.center.length === 2) return scene.center;
    if (orderedPlacements.length > 0) return [Number(orderedPlacements[0].lat), Number(orderedPlacements[0].lng)];
    return [-29.413435, -66.85677];
  }, [scene?.center, orderedPlacements]);

  useEffect(() => {
    if (!navigator.geolocation) {
      setPositionError('Este navegador no soporta geolocalización.');
      return undefined;
    }
    setTracking(true);
    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        setUserPosition([pos.coords.latitude, pos.coords.longitude]);
        setUserAccuracyMeters(Number.isFinite(pos.coords.accuracy) ? pos.coords.accuracy : null);
        setPositionError(null);
      },
      (err) => {
        console.error(err);
        setPositionError(describeGeoError(err));
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 8000 }
    );
    return () => {
      navigator.geolocation.clearWatch(watchId);
      setTracking(false);
    };
  }, []);

  const orderedWithDistance = useMemo(() => {
    return orderedPlacements.map((p) => {
      if (!userPosition) return { ...p, distanceMeters: null };
      const d = haversineMeters(
        Number(userPosition[0]),
        Number(userPosition[1]),
        Number(p.lat),
        Number(p.lng)
      );
      return { ...p, distanceMeters: d, gpsAccuracyMeters: userAccuracyMeters };
    });
  }, [orderedPlacements, userPosition, userAccuracyMeters]);

  useEffect(() => {
    const nextFiltered = {};
    for (const p of orderedWithDistance) {
      if (p.distanceMeters == null) continue;
      const prev = Number(distanceFilterRef.current[p.id]);
      const filtered = Number.isFinite(prev)
        ? prev + PROXIMITY_FILTER_ALPHA * (p.distanceMeters - prev)
        : p.distanceMeters;
      distanceFilterRef.current[p.id] = filtered;
      nextFiltered[p.id] = filtered;
    }
    setFilteredDistanceByPlacement((prev) => ({ ...prev, ...nextFiltered }));
  }, [orderedWithDistance]);

  const nearestPlacement = useMemo(() => {
    const list = orderedWithDistance.filter((p) => p.distanceMeters != null);
    if (list.length === 0) return orderedWithDistance[0] || null;
    list.sort((a, b) => {
      const da = Number(filteredDistanceByPlacement[a.id] ?? a.distanceMeters ?? Infinity);
      const db = Number(filteredDistanceByPlacement[b.id] ?? b.distanceMeters ?? Infinity);
      return da - db;
    });
    return list[0];
  }, [orderedWithDistance, filteredDistanceByPlacement]);

  const currentRouteTarget = useMemo(() => {
    if (!scene?.routeGuided) return nearestPlacement;
    const next = orderedWithDistance.find((p) => !visitedPlacementIds.includes(p.id));
    return next || null;
  }, [scene?.routeGuided, orderedWithDistance, visitedPlacementIds, nearestPlacement]);

  const routeProgress = useMemo(() => {
    if (!scene?.routeGuided) return null;
    const total = orderedPlacements.length;
    const visited = orderedPlacements.filter((p) => visitedPlacementIds.includes(p.id)).length;
    return { visited, total };
  }, [scene?.routeGuided, orderedPlacements, visitedPlacementIds]);

  const nearestFromSeed = useMemo(() => {
    if (!autoRequested || !hasSeedCoords || orderedPlacements.length === 0) return null;
    let best = null;
    for (const p of orderedPlacements) {
      const d = haversineMeters(seedLat, seedLng, Number(p.lat), Number(p.lng));
      if (!best || d < best.distanceMeters) best = { placement: p, distanceMeters: d };
    }
    return best;
  }, [autoRequested, hasSeedCoords, orderedPlacements, seedLat, seedLng]);

  useEffect(() => {
    if (!autoRequested) return;
    if (seedAutoHandledRef.current) return;
    const p = scene?.routeGuided ? currentRouteTarget : nearestFromSeed?.placement;
    if (!p) return;
    const seedDistance = hasSeedCoords
      ? haversineMeters(seedLat, seedLng, Number(p.lat), Number(p.lng))
      : null;
    if (seedDistance == null) return;
    const triggerRadius = Math.max(5, Number(p.activationRadiusMeters) || 25);
    if (seedDistance > triggerRadius + 3) return;
    seedAutoHandledRef.current = true;
    setActivePlacementSource('proximity');
    setActivePlacement(p);
  }, [autoRequested, scene?.routeGuided, currentRouteTarget, nearestFromSeed, hasSeedCoords, seedLat, seedLng, sceneId]);

  const openPlacement = useCallback((target, source) => {
    setActivePlacementSource(source);
    if (!visitorId || !target?.id) {
      setActivePlacement(target);
      return;
    }
    recordDiscovery(visitorId, { ...target, sceneId }, eventXpMultiplier)
      .then(({ isNew, xp }) => {
        if (isNew) {
          setDiscoveryXpResult(xp);
          setDiscoveredPlacement(target);
          refreshProfile();
          if (target.modelId) {
            getCountFromServer(collection(db, 'models', target.modelId, 'anchors'))
              .then((snap) => setDiscoveryFragmentsCount(snap.data().count))
              .catch(() => setDiscoveryFragmentsCount(0));
          } else {
            setDiscoveryFragmentsCount(0);
          }
        } else {
          setActivePlacement(target);
        }
      })
      .catch((e) => {
        console.error('Error registrando descubrimiento:', e);
        setActivePlacement(target);
      });
  }, [visitorId, sceneId, refreshProfile, eventXpMultiplier]);

  // Fragmentos culturales: mismas anclas (texto/imagen/audio/video/link) que el
  // admin adjunta al modelo desde "Accesorios & Anclas", ahora también visibles
  // en el flujo de descubrimiento por GPS.
  useEffect(() => {
    let alive = true;
    const modelId = activePlacement?.modelId;
    if (!modelId) {
      setActiveAnchors([]);
      return undefined;
    }
    setActiveAnchorsLoading(true);
    (async () => {
      try {
        const qRef = query(collection(db, 'models', modelId, 'anchors'), orderBy('createdAt', 'desc'));
        const snap = await getDocs(qRef);
        if (!alive) return;
        setActiveAnchors(snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })));
      } catch (e) {
        console.error('Error cargando fragmentos culturales:', e);
        if (alive) setActiveAnchors([]);
      } finally {
        if (alive) setActiveAnchorsLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [activePlacement?.modelId]);

  // Reinicia el progreso de restauración cada vez que se abre una obra nueva,
  // y une/desune la presencia en tiempo real (multijugador) para esa obra.
  useEffect(() => {
    setRestorationZones(new Set());
    setRestorationZoomed(false);
    setRestorationResult(null);
    restorationBaselineRadiusRef.current = null;
    restorationDoneRef.current = false;

    const placementId = activePlacement?.id;
    if (!placementId) {
      setPresenceList([]);
      setRestoredCommunityCount(0);
      return undefined;
    }

    const leavePresence = joinPresence(placementId, visitorId, 'Explorador');
    const unsubPresence = subscribePresence(placementId, setPresenceList);
    const unsubCount = subscribeRestoredCount(placementId, setRestoredCommunityCount);

    return () => {
      leavePresence();
      unsubPresence();
      unsubCount();
    };
  }, [activePlacement?.id, visitorId]);

  const handleCameraOrbitChange = useCallback((orbit) => {
    if (restorationDoneRef.current) return;
    const zoneIdx = zoneIndexForTheta(orbit.theta);
    if (zoneIdx != null) {
      setRestorationZones((prev) => (prev.has(zoneIdx) ? prev : new Set(prev).add(zoneIdx)));
    }
    if (restorationBaselineRadiusRef.current == null && Number.isFinite(orbit.radius)) {
      restorationBaselineRadiusRef.current = orbit.radius;
    } else if (Number.isFinite(orbit.radius) && restorationBaselineRadiusRef.current) {
      if (orbit.radius <= restorationBaselineRadiusRef.current * RESTORATION_ZOOM_RATIO) {
        setRestorationZoomed(true);
      }
    }
  }, []);

  // Completar restauración: bono de XP, +1 al contador comunitario y bono de
  // equipo si hay más de un visitante presente en esta obra en este momento.
  useEffect(() => {
    const progress = restorationZones.size + (restorationZoomed ? 1 : 0);
    if (progress < RESTORATION_TOTAL_STEPS) return;
    if (restorationDoneRef.current) return;
    if (!visitorId || !activePlacement?.id) return;
    restorationDoneRef.current = true;

    const teamBonus = presenceList.length > 1;
    markRestored(visitorId, activePlacement.id, { xpMultiplier: eventXpMultiplier, teamBonus })
      .then((result) => {
        if (result.alreadyRestored) return;
        incrementRestoredCount(activePlacement.id);
        refreshProfile();
        setRestorationResult(result);
      })
      .catch((e) => console.error('Error completando restauración:', e));
  }, [restorationZones, restorationZoomed, visitorId, activePlacement?.id, presenceList, eventXpMultiplier, refreshProfile]);

  const closeDiscoveryOverlay = () => {
    setActivePlacement(discoveredPlacement);
    setDiscoveredPlacement(null);
    setDiscoveryXpResult(null);
    setDiscoveryFragmentsCount(0);
  };

  useEffect(() => {
    const target = scene?.routeGuided ? currentRouteTarget : nearestPlacement;
    if (!target || target.distanceMeters == null) return;
    const triggerRadius = Math.max(5, Number(target.activationRadiusMeters) || 25);
    const exitRadius = triggerRadius + PROXIMITY_EXIT_BUFFER_METERS;
    const distanceForDecision = Number(filteredDistanceByPlacement[target.id] ?? target.distanceMeters);
    const accuracy = Number(target.gpsAccuracyMeters ?? userAccuracyMeters ?? Infinity);
    const hasReliableFix = Number.isFinite(accuracy) && accuracy <= PROXIMITY_MAX_GPS_ACCURACY_METERS;
    if (!hasReliableFix) return;

    const state = proximityStateRef.current[target.id] || { inside: false, enterHits: 0, exitHits: 0 };
    const isInsideNow = distanceForDecision <= triggerRadius;
    const isOutsideNow = distanceForDecision > exitRadius;

    if (isInsideNow) {
      state.enterHits += 1;
      state.exitHits = 0;
    } else if (isOutsideNow) {
      state.exitHits += 1;
      state.enterHits = 0;
    } else {
      state.enterHits = 0;
      state.exitHits = 0;
    }
    proximityStateRef.current[target.id] = state;

    const now = Date.now();
    const lastOpenAt = Number(proximityLastOpenRef.current[target.id] || 0);
    const canOpenNow = now - lastOpenAt >= PROXIMITY_REOPEN_COOLDOWN_MS;
    const enoughToEnter = !state.inside && state.enterHits >= PROXIMITY_ENTER_CONFIRM_SAMPLES;
    const enoughToExit = state.inside && state.exitHits >= PROXIMITY_EXIT_CONFIRM_SAMPLES;

    if (enoughToEnter && canOpenNow) {
      state.inside = true;
      state.enterHits = 0;
      state.exitHits = 0;
      proximityLastOpenRef.current[target.id] = now;
      proximityOpenStartedAtRef.current[target.id] = now;
      if (scene?.routeGuided && scene?.routeAutoAdvance) {
        setVisitedPlacementIds((prev) => (prev.includes(target.id) ? prev : [...prev, target.id]));
      }
      openPlacement(target, 'proximity');
      return;
    }

    if (
      enoughToExit
      && !arPresenting
      && activePlacementSource === 'proximity'
      && activePlacement?.id === target.id
      && now - arLastStopAtRef.current > PROXIMITY_AR_RETURN_GRACE_MS
      && now - Number(proximityOpenStartedAtRef.current[target.id] || 0) > PROXIMITY_MIN_OPEN_MS
    ) {
      state.inside = false;
      state.enterHits = 0;
      state.exitHits = 0;
      setActivePlacement(null);
    }
  }, [
    scene?.routeGuided,
    scene?.routeAutoAdvance,
    currentRouteTarget,
    nearestPlacement,
    filteredDistanceByPlacement,
    userAccuracyMeters,
    arPresenting,
    activePlacementSource,
    activePlacement,
    openPlacement
  ]);

  useEffect(() => {
    if (activePlacementSource !== 'proximity' || !activePlacement || !userPosition) return;
    if (arPresenting) return;
    const distance = haversineMeters(
      Number(userPosition[0]),
      Number(userPosition[1]),
      Number(activePlacement.lat),
      Number(activePlacement.lng)
    );
    const radius = Math.max(5, Number(activePlacement.activationRadiusMeters) || 25);
    const now = Date.now();
    const openAt = Number(proximityOpenStartedAtRef.current[activePlacement.id] || 0);
    const closeAllowed = now - openAt > PROXIMITY_MIN_OPEN_MS && now - arLastStopAtRef.current > PROXIMITY_AR_RETURN_GRACE_MS;
    if (closeAllowed && distance > radius + PROXIMITY_EXIT_BUFFER_METERS) {
      const st = proximityStateRef.current[activePlacement.id] || { inside: false, enterHits: 0, exitHits: 0 };
      st.inside = false;
      st.enterHits = 0;
      st.exitHits = PROXIMITY_EXIT_CONFIRM_SAMPLES;
      proximityStateRef.current[activePlacement.id] = st;
      setActivePlacement(null);
    }
  }, [activePlacementSource, activePlacement, userPosition, arPresenting]);

  const markCurrentAsVisited = () => {
    if (!currentRouteTarget) return;
    setVisitedPlacementIds((prev) => (prev.includes(currentRouteTarget.id) ? prev : [...prev, currentRouteTarget.id]));
  };
  const resetRouteProgress = () => setVisitedPlacementIds([]);

  if (loading) return <div className="geo-admin-empty">Cargando visor geográfico...</div>;
  if (error) return <div className="geo-admin-empty">{error}</div>;
  if (!scene) return null;

  const bannerTarget = scene.routeGuided ? currentRouteTarget : nearestPlacement;
  const diagnosticTarget = bannerTarget;
  const diagnosticState = diagnosticTarget
    ? (proximityStateRef.current[diagnosticTarget.id] || { inside: false, enterHits: 0, exitHits: 0 })
    : { inside: false, enterHits: 0, exitHits: 0 };
  const diagnosticRawDistance = diagnosticTarget?.distanceMeters ?? null;
  const diagnosticFilteredDistance = diagnosticTarget
    ? Number(filteredDistanceByPlacement[diagnosticTarget.id] ?? diagnosticRawDistance ?? NaN)
    : null;
  const diagnosticRadius = Math.max(5, Number(diagnosticTarget?.activationRadiusMeters) || 25);
  const diagnosticExitRadius = diagnosticRadius + PROXIMITY_EXIT_BUFFER_METERS;

  // Monitor de acercamiento: distancia restante + orientación respecto al
  // objetivo actual, pensado para verse claro sobre el mapa en el celular.
  const trackerDistanceForDisplay = bannerTarget
    ? Number(filteredDistanceByPlacement[bannerTarget.id] ?? bannerTarget.distanceMeters ?? NaN)
    : null;
  const trackerRemainingMeters = (bannerTarget && Number.isFinite(trackerDistanceForDisplay))
    ? Math.max(0, Math.round(trackerDistanceForDisplay - diagnosticRadius))
    : null;
  const trackerReached = bannerTarget && Number.isFinite(trackerDistanceForDisplay)
    ? trackerDistanceForDisplay <= diagnosticRadius
    : false;
  const bearingToTarget = (bannerTarget && userPosition
    && Number.isFinite(Number(bannerTarget.lat)) && Number.isFinite(Number(bannerTarget.lng)))
    ? bearingDegrees(Number(userPosition[0]), Number(userPosition[1]), Number(bannerTarget.lat), Number(bannerTarget.lng))
    : null;
  const directionToTarget = bearingToTarget == null ? null : (bearingToTarget - (compassHeading ?? 0) + 360) % 360;
  const isAligned = compassEnabled && directionToTarget != null
    && (directionToTarget <= ORIENTATION_ALIGNED_THRESHOLD_DEG || directionToTarget >= 360 - ORIENTATION_ALIGNED_THRESHOLD_DEG);

  return (
    <div className="geo-viewer-wrap">
      <header className="geo-public-header geo-viewer-header">
        <button className="geo-icon-btn geo-viewer-back" onClick={() => navigate('/geoscenes')} title="Volver a escenas">
          <i className="bi bi-arrow-left" />
        </button>
        <div className="geo-viewer-header-title">
          <h1>{scene.name || 'Escena geográfica'}</h1>
        </div>
        <button
          className={`geo-icon-btn ${diagnosticMode ? 'active' : ''}`}
          onClick={() => {
            const next = !diagnosticMode;
            setDiagnosticMode(next);
            try { localStorage.setItem(GEO_DIAGNOSTIC_MODE_KEY, next ? '1' : '0'); } catch {}
          }}
          title={diagnosticMode ? 'Diagnóstico ON' : 'Diagnóstico OFF'}
        >
          <i className="bi bi-bug" />
        </button>
      </header>

      <ActiveEventBanner />

      <div className="geo-target-bar">
        {bannerTarget ? (
          <>
            <span className="geo-target-bar-label">
              <i className="bi bi-geo-alt-fill" />
              {scene.routeGuided ? 'Siguiente' : 'Más cercano'}: <b>{bannerTarget.title || bannerTarget.modelName || 'Modelo'}</b>
            </span>
            <div className="geo-actions-inline">
              {scene.routeGuided && (
                <button className="geo-btn geo-btn-secondary" onClick={markCurrentAsVisited}>Marcar visitado</button>
              )}
              <button
                className="geo-btn geo-btn-primary"
                onClick={() => openPlacement(bannerTarget, 'manual')}
              >
                Abrir ahora
              </button>
            </div>
          </>
        ) : (
          <span>{tracking ? 'Buscando puntos cercanos...' : 'Sin seguimiento de ubicación.'}</span>
        )}
      </div>

      <div className="geo-viewer-grid">
        <div className="geo-map-card">
          {bannerTarget && (
            <div className={`geo-tracker-hud ${trackerReached ? 'is-reached' : ''}`}>
              <div className="geo-tracker-distance">
                {trackerReached ? (
                  <span className="geo-tracker-reached"><i className="bi bi-check-circle-fill" /> Llegaste</span>
                ) : trackerRemainingMeters == null ? (
                  <span className="geo-tracker-calculating">Calculando distancia...</span>
                ) : (
                  <>
                    <span className="geo-tracker-distance-value">{trackerRemainingMeters}</span>
                    <span className="geo-tracker-distance-unit">m para llegar</span>
                  </>
                )}
              </div>

              <div className="geo-tracker-compass">
                {!compassEnabled ? (
                  <button type="button" className="geo-btn geo-btn-secondary geo-tracker-compass-btn" onClick={requestCompassPermission}>
                    <i className="bi bi-compass" /> Activar brújula
                  </button>
                ) : (
                  <>
                    <span className={`geo-tracker-arrow-wrap ${isAligned ? 'is-aligned' : ''}`}>
                      <i
                        className="bi bi-arrow-up-circle-fill geo-tracker-arrow"
                        style={{ transform: `rotate(${directionToTarget ?? 0}deg)` }}
                      />
                    </span>
                    <span className={`geo-tracker-orientation-text ${isAligned ? 'is-aligned' : ''}`}>
                      {trackerReached
                        ? 'Ya estás en la zona'
                        : isAligned
                          ? 'Vas bien orientado'
                          : 'Girá hacia la flecha'}
                    </span>
                  </>
                )}
              </div>
              {compassError && <p className="geo-tracker-error">{compassError}</p>}
            </div>
          )}
          <GeoSceneMap
            center={mapCenter}
            placements={scene.routeGuided ? orderedPlacements : placements}
            routeGuided={!!scene.routeGuided}
            currentRouteTargetId={currentRouteTarget?.id || null}
            visitedPlacementIds={visitedPlacementIds}
            userPosition={userPosition}
            onMarkerClick={(p) => openPlacement(p, 'manual')}
          />
        </div>

        <aside className="geo-card">
          <h3>{scene.routeGuided ? 'Ruta de puntos' : 'Puntos de interés'}</h3>
          {(scene.routeGuided ? orderedPlacements : placements).length === 0 ? (
            <p className="geo-muted">No hay modelos geolocalizados cargados.</p>
          ) : (
            <div className="geo-placement-list">
              {(scene.routeGuided ? orderedPlacements : placements).map((p, idx) => (
                <article key={p.id} className="geo-placement-item">
                  <div>
                    <strong>{scene.routeGuided ? `#${Math.max(1, Number(p.routeOrder) || idx + 1)} ` : ''}{p.title || p.modelName || 'Modelo'}</strong>
                    <p>{Number(p.lat).toFixed(6)}, {Number(p.lng).toFixed(6)}</p>
                  </div>
                  <button
                    className="geo-btn geo-btn-primary"
                    onClick={() => openPlacement(p, 'manual')}
                  >
                    AR
                  </button>
                </article>
              ))}
            </div>
          )}
        </aside>
      </div>

      {scene.routeGuided && routeProgress && (
        <div className="geo-route-progress">
          <span>Ruta guiada: {routeProgress.visited} / {routeProgress.total} visitados</span>
          <button className="geo-btn geo-btn-secondary" onClick={resetRouteProgress}>Reiniciar ruta</button>
        </div>
      )}

      {positionError && <div className="geo-muted geo-footer-note">{positionError}</div>}

      {diagnosticMode && (
        <section className="geo-diagnostic-panel">
          <h4>Diagnóstico proximidad (visor gráfico)</h4>
          <p>
            Objetivo: <b>{diagnosticTarget?.title || diagnosticTarget?.modelName || 'N/A'}</b>
          </p>
          <p>
            Distancia cruda: {diagnosticRawDistance == null ? 'N/A' : `${diagnosticRawDistance.toFixed(2)} m`} ·
            filtrada: {diagnosticFilteredDistance == null || Number.isNaN(diagnosticFilteredDistance) ? 'N/A' : `${diagnosticFilteredDistance.toFixed(2)} m`}
          </p>
          <p>
            Accuracy: {userAccuracyMeters == null ? 'N/A' : `±${Math.round(userAccuracyMeters)} m`} ·
            enter: {diagnosticRadius.toFixed(1)} m · exit: {diagnosticExitRadius.toFixed(1)} m
          </p>
          <p>
            inside: {diagnosticState.inside ? 'true' : 'false'} · enterHits: {diagnosticState.enterHits} · exitHits: {diagnosticState.exitHits}
          </p>
          <p>
            AR presentando: {arPresenting ? 'true' : 'false'} · cooldown re-open: {PROXIMITY_REOPEN_COOLDOWN_MS} ms
          </p>
        </section>
      )}

      <Dialog
        open={!!activePlacement}
        onClose={() => setActivePlacement(null)}
        fullScreen
        maxWidth="xl"
        PaperProps={{ sx: { backgroundColor: 'black', overflow: 'hidden', height: '100dvh' } }}
      >
        {activePlacement && (
          <VisorRA
            initialModelUrl={activePlacement.modelUrl}
            isMobile={typeof window !== 'undefined' ? window.innerWidth < 768 : false}
            onClose={() => setActivePlacement(null)}
            autoEnterAr={activePlacementSource === 'proximity'}
            initialScale={Number(activePlacement.scale) || 1}
            initialRotationY={Number(activePlacement.rotationY) || 0}
            initialArTransform={activePlacement.arTransform || null}
            persistModelTransform={false}
            anchors={activeAnchors}
            anchorsLoading={activeAnchorsLoading}
            onCameraOrbitChange={handleCameraOrbitChange}
            onArStatusChange={(status) => {
              if (status === 'session-started') {
                setArPresenting(true);
                return;
              }
              if (status === 'session-ended' || status === 'not-presenting' || status === 'failed') {
                setArPresenting(false);
                arLastStopAtRef.current = Date.now();
              }
            }}
            publicMode
          />
        )}
        {activePlacement && !restorationResult && (
          <RestorationHud
            visitedZones={restorationZones}
            zoomed={restorationZoomed}
            presenceCount={presenceList.length}
            restoredCount={restoredCommunityCount}
          />
        )}
      </Dialog>

      <DiscoveryOverlay
        placement={discoveredPlacement}
        xpResult={discoveryXpResult}
        fragmentsCount={discoveryFragmentsCount}
        onClose={closeDiscoveryOverlay}
      />

      <RestorationCompleteOverlay
        result={restorationResult}
        placementTitle={activePlacement?.title || activePlacement?.modelName || 'la obra'}
        onClose={() => setRestorationResult(null)}
      />
    </div>
  );
}
